import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { WifiSlash, CloudArrowUp, CloudCheck } from "@phosphor-icons/react";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { onSyncStateChange, getPendingCount } from "../lib/syncEngine";

// Small persistent indicator mounted once in ModuleNavBar (shared by every
// module page, desktop and mobile) - shows offline mode, how many local
// edits are still queued, and a brief "synced" confirmation once they land.
export function OfflineStatusBadge() {
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);

  useEffect(() => {
    getPendingCount().then(setPending);
    return onSyncStateChange(({ pending: p, syncing: s }) => {
      setPending((prevPending) => {
        if (prevPending > 0 && p === 0 && !s) {
          setJustSynced(true);
          setTimeout(() => setJustSynced(false), 2500);
        }
        return p;
      });
      setSyncing(s);
    });
  }, []);

  if (!isOnline) {
    return (
      <div
        data-testid="offline-status-badge"
        title={t("offline.offlineHint")}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-amber-500/15 text-amber-600 border border-amber-500/30 ml-auto shrink-0"
      >
        <WifiSlash size={13} weight="bold" />
        {t("offline.offlineBadge")}
        {pending > 0 && <span data-testid="offline-pending-count">({pending})</span>}
      </div>
    );
  }

  if (syncing || pending > 0) {
    return (
      <div
        data-testid="offline-sync-status-badge"
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-secondary text-muted-foreground border border-border ml-auto shrink-0"
      >
        <CloudArrowUp size={13} className="animate-pulse" />
        {t("offline.syncingBadge", { count: pending })}
      </div>
    );
  }

  if (justSynced) {
    return (
      <div
        data-testid="offline-synced-status-badge"
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 ml-auto shrink-0"
      >
        <CloudCheck size={13} weight="bold" />
        {t("offline.syncedBadge")}
      </div>
    );
  }

  return null;
}
